import { env } from "@klarify/env";
import mixpanel from "mixpanel-browser";

const MIXPANEL_TOKEN = env.NEXT_PUBLIC_MIXPANEL_TOKEN;
const MIXPANEL_API_HOST = env.NEXT_PUBLIC_MIXPANEL_API_HOST;

let initialized = false;

export function initMixpanel() {
	if (!MIXPANEL_TOKEN) {
		console.warn("Mixpanel token is missing! Check your .env file.");
		return;
	}

	mixpanel.init(MIXPANEL_TOKEN, {
		autocapture: true,
		opt_out_tracking_by_default: true,
		...(MIXPANEL_API_HOST && { api_host: MIXPANEL_API_HOST }),
	});
	initialized = true;
}

/**
 * Identify the current user in Mixpanel and set their People profile properties.
 * Uses Mixpanel reserved `$`-prefixed properties for auto-mapping to People fields.
 * No-ops if Mixpanel has not been initialized yet.
 */
export function identifyMixpanelUser(
	userId: string,
	properties?: Record<string, unknown>,
) {
	if (!initialized) {
		return;
	}
	mixpanel.identify(userId);
	if (properties) {
		mixpanel.people.set(properties);
	}
}

/**
 * Track a client-side event in Mixpanel.
 *
 * The browser SDK automatically attaches the current `distinct_id`
 * (set via {@link identifyMixpanelUser}), so there is no need to
 * pass a user id explicitly.
 *
 * No-ops if Mixpanel has not been initialized yet.
 */
export function trackClientEvent(
	event: string,
	properties?: Record<string, unknown>,
) {
	if (!initialized) {
		return;
	}
	mixpanel.track(event, properties);
}

/**
 * Register super properties that will be sent with every subsequent `track` call.
 * Useful for attaching user plan info so it's included on all client-side events.
 * No-ops if Mixpanel has not been initialized yet.
 */
export function registerSuperProperties(properties: Record<string, unknown>) {
	if (!initialized) {
		return;
	}
	mixpanel.register(properties);
}

/**
 * Reset Mixpanel identity (call on sign-out to generate a new anonymous distinct_id).
 * No-ops if Mixpanel has not been initialized yet.
 */
export function resetMixpanelUser() {
	if (!initialized) {
		return;
	}
	mixpanel.reset();
}

/**
 * Opt the current user out of Mixpanel tracking.
 * Once called, all subsequent `track`, `identify`, and `people.set` calls
 * become no-ops until {@link optInMixpanelTracking} is called.
 * No-ops if Mixpanel has not been initialized yet.
 */
export function optOutMixpanelTracking() {
	if (!initialized) {
		return;
	}
	if (!mixpanel.has_opted_out_tracking()) {
		mixpanel.opt_out_tracking();
	}
}

/**
 * Opt the current user back in to Mixpanel tracking after a previous opt-out.
 * No-ops if Mixpanel has not been initialized yet or the user is already opted in.
 */
export function optInMixpanelTracking() {
	if (!initialized) {
		return;
	}
	if (!mixpanel.has_opted_in_tracking()) {
		mixpanel.opt_in_tracking();
	}
}
